import { catalog } from './core/catalog.js';
import { bindCardLongPress } from './ui/card-longpress.js';
import { customOrder } from './core/custom-order.js';
import { orderIds, moveChannelIds } from './core/order-core.js';
import { currentGroups } from './ui/order-editor.js';
import { epg } from './core/epg.js';
import { profiles } from './core/profiles.js';
import { account } from './core/account.js';
import { viewers } from './core/viewers.js';
import { settings, storage } from './core/storage.js';
import { shellById, applyShell } from './core/shells.js';
import { startTrail, readTrail, clearTrail } from './core/trail.js';
import { skinById } from './core/skins.js';
import { updater } from './core/updater.js';
import { support } from './core/support.js';
import { contentBlock } from './core/content-block.js';
import { device } from './core/device.js';
import { i18n, t, loadLocale } from './core/i18n.js';
import { notify } from './core/notify.js';
import { askSecondFactor } from './ui/twofa.js';
import { createCatalogClient } from './core/catalog-client.js';
import { shareContent, channelShareText, shareLink, previewLink } from './core/share.js';
import { coach } from './core/coach.js';
import { tvnav } from './core/tvnav.js';
import { PlayerUI } from './core/player-ui.js';
import { initSharedUse, beatSources } from './core/shared-use.js';
import { inviteWebBeat } from './core/invite-web.js';
import { initUiSounds } from './core/ui-sounds.js';
import { initTizen } from './core/tizen.js';
import { startAutoRefresh } from './core/auto-refresh.js';
import { ensureHls } from './core/player.js';
import { parseAge } from './core/age.js';
import { parseEntryUrl, watchUrl, syncUrl, urlSyncEnabled, whenCatalogLoaded, resolveToken, appPath } from './core/deeplink.js';
import { refreshFavButtons, lazyImages } from './ui/render.js';
import { initPeek, hidePeek } from './ui/peek.js';
import { previewMuted, setPreviewMuted } from './core/preview-sound.js';
import { initWarmup } from './core/warmup.js';
import { offline, setupOffline } from './core/offline.js';
import { net } from './core/net.js';
import { transcodeUrl, initLocalTranscode } from './core/transcode.js';
import { handleOfflineClick, refreshOfflineButtons } from './ui/offline-ui.js';
import { hooks as clientHooks } from './core/clients.js';
import { openCollection } from './pages/collection.js';
import { openMatch } from './pages/match.js';
import { openReportsAdmin } from './pages/reports-admin.js';
import { tvscale } from './core/tvscale.js';
import { escapeHtml, cleanName } from './core/utils.js';

import homePage from './pages/home.js';
import livePage from './pages/live.js';
import { moviesPage, seriesPage } from './pages/library.js';
import { movieDetailPage, seriesDetailPage, setDetailSiblings } from './pages/details.js';
import searchPage from './pages/search.js';
import mylistPage from './pages/mylist.js';
import loginPage from './pages/login.js';
import { openAddSource } from './pages/add-source.js';
import startPage, { firstScreen } from './pages/start.js';
import settingsPage, { preferSettingsTab } from './pages/settings.js';
import pairPage from './pages/pair.js';
import whoPage, { avatarHtml, whoAuto } from './pages/who.js';
import guidePage from './pages/guide.js';
import actorPage from './pages/actor.js';
import recordingsPage from './pages/recordings.js';
import offlinePage from './pages/offline.js';

import { maybeStartIntro, skipIntro, introActive } from './core/intro.js';
import { openExternal } from './core/external.js';
import welcomePage, { shouldShowWelcome, welcomeDone } from './pages/welcome.js';
import termsPage from './pages/terms.js';
import { needsTermsGate, termsRecord, saveTermsRecord, currentTermsVersion } from './core/terms.js';

const VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';
const PAGES = [termsPage, homePage, livePage, moviesPage, seriesPage, movieDetailPage, seriesDetailPage, searchPage, mylistPage, loginPage, startPage, settingsPage, pairPage, whoPage, guidePage, actorPage, recordingsPage, offlinePage, welcomePage];

let app = null;
let player = null;

/* ---------------- Rutas ---------------- */

/** Pantalla de «la demo ha terminado» (la define el arranque); la usan el guardia de rutas y los avisos de sesión perdida. */
let demoEndHook = null;

function buildRoutes() {
    const routes = PAGES.map((mod) => ({
        path: mod.path,
        alias: mod.alias,
        mod,
        async({ to, resolve, reject }) {
            // La demo pública no gestiona nada: espectadores, fuentes, emparejado, grabaciones y bienvenida la devuelven al inicio.
            if (account.demo && /^\/(who|login|profile|pair|recordings|start|welcome|offline)\//.test(String(mod.path))) { reject(); app.views.main.router.navigate('/', { reloadAll: true }); return; }
            // Una carga de la demo sin sesión (terminada, caducada o perdida) nunca enseña el inicio de sesión.
            if (account.demoEntry && !account.demo && /^\/login\//.test(String(mod.path))) { reject(); if (demoEndHook) demoEndHook(!!account.demoLimited); return; }
            resolve({ content: mod.render(to) });
        },
        on: {
            pageInit(e, page) {
                const route = page.route || {};
                enablePullToRefresh(page);
                const result = mod.mount(page, route);
                Promise.resolve(result).then((unmount) => { page.el._unmount = unmount; });
            },
            pageBeforeRemove(e, page) {
                if (typeof page.el._unmount === 'function') {
                    try { page.el._unmount(); } catch (err) { console.warn(err); }
                }
            }
        }
    }));
    routes.push({ path: '(.*)', redirect: '/' });
    return routes;
}

/* ---------------- Tirar para actualizar (móvil) ---------------- */

function enablePullToRefresh(page) {
    if (device.isTV || !device.hasTouch) return;
    const content = page.el.querySelector('.page-content');
    if (!content || page.el.classList.contains('no-nav')) return;
    content.classList.add('ptr-content');
    content.insertAdjacentHTML('afterbegin', '<div class="ptr-preloader"><div class="preloader"><div class="spinner"></div></div><div class="ptr-arrow"></div></div>');
    app.ptr.create(content);
    content.addEventListener('ptr:refresh', async () => {
        try {
            if (account.signedIn) await account.sync().catch(() => {});
            if (catalog.profile) {
                await catalog.refresh();
                catalog.loadEpg({ force: true }).catch(() => {});
            }
        } finally {
            app.ptr.done(content);
        }
    });
}

/* ---------------- Barra de navegación ---------------- */

const NAV_ITEMS = [
    { key: 'home', href: '/', icon: 'home', label: 'nav.home' },
    { key: 'live', href: '/live/', icon: 'live_tv', label: 'nav.live' },
    { key: 'movies', href: '/movies/', icon: 'movie', label: 'nav.movies' },
    { key: 'series', href: '/series/', icon: 'tv', label: 'nav.series' },
    { key: 'guide', href: '/guide/', icon: 'calendar_view_day', label: 'nav.guide', desktopOnly: true },
    { key: 'search', href: '/search/', icon: 'search', label: 'nav.search', mobileOnly: true },
    { key: 'mylist', href: '/mylist/', icon: 'bookmark', label: 'nav.mylist', desktopOnly: true },
    // Solo si el servidor tiene las grabaciones activadas (se sabe tras account.init()).
    { key: 'recordings', href: '/recordings/', icon: 'fiber_manual_record', label: 'rec.title', desktopOnly: true, when: () => account.canRecord }
];

function navHtml() {
    return `<nav class="appnav" data-nav-row aria-label="Main">
        <a class="appnav-brand" href="#" data-nav="home" data-nav-skip><img src="img/logo.png" alt=""/><span>${t('app.name')}</span></a>
        <div class="appnav-links">
            ${NAV_ITEMS.filter((n) => !n.when || n.when()).map((n) => `<a class="appnav-link ${n.desktopOnly ? 'nav-desktop-only' : ''} ${n.mobileOnly ? 'nav-mobile-only' : ''}" href="#" data-nav="${n.key}" data-href="${n.href}"><span class="mi">${n.icon}</span><span>${t(n.label)}</span></a>`).join('')}
        </div>
        <div class="appnav-right">
            <a class="appnav-icon-btn" href="#" data-nav="search" data-href="/search/" aria-label="${t('nav.search')}"><span class="mi">search</span></a>
            ${offline.supported ? `<a class="appnav-icon-btn" href="#" data-nav="offline" data-href="/offline/" aria-label="${t('offline.title')}"><span class="mi">download</span></a>` : ''}
            <a class="appnav-icon-btn" href="#" data-nav="settings" data-href="/settings/" aria-label="${t('nav.settings')}"><span class="mi">settings</span></a>
            ${account.demo ? '' : `<a class="appnav-icon-btn appnav-avatar" href="#" data-href="/who/" data-nav-avatar aria-label="${t('settings.switchViewer')}">${avatarHtml(viewers.current(), 'is-small')}</a>`}
        </div>
    </nav>`;
}

/** Menú del espectador en móvil: se despliega desde el avatar de Inicio (o el ☰ de cada sección)
    con lo que no cabe en la barra inferior. Los enlaces usan data-href (manejador global). */
let userMenuSeq = 0;
function openMainMenu(target) {
    const viewer = viewers.current() || catalog.viewer;
    const entry = (icon, label, href) => `<a class="user-menu-item" href="#" data-href="${href}"><span class="mi">${icon}</span><span>${label}</span></a>`;
    const items = [
        entry('bookmark', t('nav.mylist'), '/mylist/'),
        entry('calendar_view_day', t('nav.guide'), '/guide/')
    ];
    if (account.canRecord) items.push(entry('fiber_manual_record', t('rec.title'), '/recordings/'));
    if (offline.supported) items.push(entry('download', t('offline.title'), '/offline/'));
    items.push(entry('settings', t('nav.settings'), '/settings/'));
    const email = account.signedIn && account.user ? account.user.email || '' : '';
    const id = 'user-menu-' + (++userMenuSeq);
    const popover = app.popover.create({
        targetEl: target,
        arrow: false,
        content: `<div class="popover user-menu" id="${id}"><div class="popover-inner">
            <div class="user-menu-head">${avatarHtml(viewer, 'is-small')}<span class="user-menu-who"><strong>${escapeHtml(viewer ? viewer.name : t('app.name'))}</strong>${email ? `<small>${escapeHtml(email)}</small>` : ''}</span></div>
            ${items.join('')}
            <div class="user-menu-sep"></div>
            ${entry('switch_account', t('settings.switchViewer'), '/who/')}
        </div></div>`,
        on: {
            closed() {
                try { popover.destroy(); } catch (e) { /* ignore */ }
                const el = document.getElementById(id);
                if (el && el.parentNode) el.parentNode.removeChild(el);
            },
            // Si el cierre se interrumpe (se navega a la entrada pulsada mientras se cierra, o la pestaña pasa a segundo plano)
            // «closed» no llega y el menú se quedaba invisible pero tapando lo de debajo: plazo de seguridad.
            close() {
                setTimeout(() => {
                    const el = document.getElementById(id);
                    if (!el) return;
                    try { popover.destroy(); } catch (e) { /* ya destruido */ }
                    if (el.parentNode) el.parentNode.removeChild(el);
                }, 700);
            }
        }
    });
    popover.open();
}

/** Ventanas de Framework7 que se quedaron a medio cerrar (sin «closed»): fuera, que tapan lo de debajo. */
function sweepGhostModals() {
    document.querySelectorAll('.popover.modal-out, .popup.modal-out, .sheet-modal.modal-out, .actions-modal.modal-out, .dialog.modal-out').forEach((el) => {
        try { if (el.f7Modal) el.f7Modal.destroy(); } catch (e) { /* ya destruida */ }
        if (el.parentNode) el.parentNode.removeChild(el);
    });
}

/** Menú ⋮ de una tarjeta de "Continuar viendo". */
/** Opciones de un canal (OK mantenido con el mando; en táctil el «+» de la tarjeta ya está a mano). */
/** Mueve un canal dentro de su hilera de categoría (menú de pulsación larga) y pasa la lista a «Mi orden». */
function moveChannelInRow(rowEl, id, where) {
    const sectionEl = rowEl && rowEl.closest('[data-row-id^="cat-"]');
    if (!sectionEl) return;
    const catId = sectionEl.getAttribute('data-row-id').slice(4);
    const shown = Array.from(sectionEl.querySelectorAll('[data-play-live]')).map((c) => c.getAttribute('data-play-live'));
    const group = currentGroups('live').find((g) => String(g.id) === catId);
    if (!group) return;
    const full = group.items.map((c) => String(c.id));
    // Si la lista no estaba ya en «Mi orden», se parte del orden que se ve ahora en esa hilera.
    const base = settings.get('liveSort') === 'mine' ? full : orderIds(full, shown);
    const next = moveChannelIds(base, shown, id, where);
    if (next.join('|') === base.join('|')) return notify.toast(t('order.cantMove'), { icon: 'info', duration: 2500 });
    customOrder.set('live', { chans: { [catId]: next } });
    settings.set('liveSort', 'mine');
    document.dispatchEvent(new CustomEvent('iptv:live-order'));
}

function openChannelMenu(id, cardEl = null) {
    const ch = catalog.get('live', id);
    if (!ch) return;
    const inRow = !!(cardEl && cardEl.closest && cardEl.closest('[data-row-id^="cat-"]'));
    const fav = catalog.isFavorite('live', id);
    const entry = (icon, label, onClick) => ({ text: `<span class="mi">${icon}</span>${label}`, onClick });
    app.actions.create({ cssClass: 'main-menu', buttons: [[
        { text: escapeHtml(cleanName(ch.name)), label: true },
        entry('play_arrow', t('common.play'), () => actions.playLive(id)),
        entry(fav ? 'bookmark_remove' : 'bookmark_add', fav ? t('common.favoriteRemove') : t('common.favoriteAdd'), () => actions.toggleFavorite('live', id)),
        device.isTV ? null : entry('share', t('detail.share'), () => { const now = epg.nowNext(ch.epgId).now; shareContent({ title: cleanName(ch.name) + ' · ' + t('app.name'), text: channelShareText(ch, now ? now.title : '', { link: previewLink('live', { title: cleanName(ch.name), program: now ? now.title : '', internal: shareLink('live', id, (k, i) => catalog.uidOf(k, i)) ? '/watch/live/' + catalog.uidOf('live', id) : '' }) }) }); }),
        ch.epgId ? entry('calendar_view_day', t('live.guide'), () => app.views.main.router.navigate('/guide/')) : null,
        // En una hilera de categoría: mover este canal a otro sitio (se guarda como «Mi orden»).
        inRow ? entry('first_page', t('order.moveFirst'), () => moveChannelInRow(cardEl, id, 'first')) : null,
        inRow ? entry('chevron_left', t('order.moveLeft'), () => moveChannelInRow(cardEl, id, 'left')) : null,
        inRow ? entry('chevron_right', t('order.moveRight'), () => moveChannelInRow(cardEl, id, 'right')) : null,
        inRow ? entry('last_page', t('order.moveLast'), () => moveChannelInRow(cardEl, id, 'last')) : null,
        // Con el mando no se alcanza el botón de sonido de la vista previa: aquí se cambia el sonido de todas las vistas previas.
        (device.isTV || device.hasHover) && settings.get('autoplayPreview') !== false ? entry(previewMuted() ? 'volume_up' : 'volume_off', t(previewMuted() ? 'preview.soundOn' : 'preview.soundOff'), () => setPreviewMuted(!previewMuted())) : null
    ].filter(Boolean), [{ text: t('common.cancel'), color: 'red' }]] }).open();
}

function openCardMenu(kind, id, { inContinue = true } = {}) {
    const item = catalog.get(kind, id);
    if (!item) return;
    const fav = catalog.isFavorite(kind, id);
    const entry = (icon, label, onClick) => ({ text: `<span class="mi">${icon}</span>${label}`, onClick });
    const buttons = [
        { text: escapeHtml(item.name), label: true },
        entry('play_arrow', t('common.resume'), () => actions.playFromCatalog(kind, id)),
        entry('info', t('common.info'), () => app.views.main.router.navigate((kind === 'vod' ? '/movie/' : '/series/') + id + '/')),
        entry(fav ? 'bookmark_remove' : 'bookmark_add', fav ? t('common.favoriteRemove') : t('common.favoriteAdd'), () => actions.toggleFavorite(kind, id)),
        kind === 'vod' ? entry(catalog.isWatched('vod', id) ? 'visibility_off' : 'visibility', catalog.isWatched('vod', id) ? t('watched.markUnwatched') : t('watched.markWatched'), async () => {
            const on = !catalog.isWatched('vod', id);
            await catalog.setWatched('vod', id, on);
            notify.toast(t(on ? 'watched.toastWatched' : 'watched.toastUnwatched'), { icon: on ? 'visibility' : 'visibility_off', duration: 1800 });
        }) : null,
        inContinue ? entry('remove_circle_outline', t('home.removeContinue'), async () => {
            await catalog.removeFromContinue(kind, id);
            notify.toast(t('home.removedContinue'), { icon: 'check', duration: 1500 });
        }) : null
    ];
    app.actions.create({ cssClass: 'main-menu', buttons: [buttons.filter(Boolean), [{ text: t('common.cancel'), color: 'red' }]] }).open();
}

function setActiveNav(key) {
    document.querySelectorAll('.appnav [data-nav]').forEach((el) => {
        el.classList.toggle('is-active', el.getAttribute('data-nav') === key);
    });
}

function navigateSection(href) {
    const router = app.views.main.router;
    // Cambiar de sección cierra cualquier selector/diálogo abierto (viven fuera del router).
    document.querySelectorAll('.modal-in:not(.toast)').forEach((el) => {
        if (el.classList.contains('popup')) app.popup.close(el);
        else if (el.classList.contains('dialog')) app.dialog.close(el);
        else if (el.classList.contains('actions-modal')) app.actions.close(el);
        else if (el.classList.contains('sheet-modal')) app.sheet.close(el);
        else if (el.classList.contains('popover')) app.popover.close(el);
    });
    if (router.currentRoute && router.currentRoute.url === href) {
        const content = document.querySelector('.page-current .page-content');
        if (content) content.scrollTo({ top: 0, behavior: 'smooth' });
        return;
    }
    router.navigate(href, { reloadAll: true, clearPreviousHistory: true });
}

/* ---------------- Acciones globales ---------------- */

let selectViewerSeq = 0;
let afterTerms = '/'; // a dónde se sigue al aceptar los términos (lo que tocaba al arrancar)

const actions = {
    version: VERSION,

    async activateProfile(profile) {
        profiles.setCurrent(profile.id);
        catalog.use(profile).catch((err) => {
            console.error(err);
            notify.error(t('common.errorLoad'));
        });
    },

    playLive(id, { list } = {}) {
        const channel = catalog.get('live', id);
        if (!channel) return;
        // Desde «TV en directo» el reproductor usa los canales que se ven con los filtros elegidos (proveedor, país, categorías…).
        const fromLive = !list && catalog.zapList && document.querySelector('.page-current[data-name="live"]') && catalog.zapList.some((c) => c.id === channel.id);
        const zapList = list || (fromLive ? catalog.zapList : channel.categoryId ? catalog.byCategory('live', channel.categoryId) : catalog.visible('live'));
        player.open({ mode: 'live', item: channel, list: zapList.length ? zapList : catalog.visible('live') });
    },

    async playMovie(id, position) {
        const item = catalog.get('vod', id);
        if (!item) return;
        const info = await catalog.vodInfo(id);
        if (catalog.ageBlocked(info)) return notify.error(t('age.blockedToast', { label: parseAge(info.age).label }));
        player.open({ mode: 'vod', item: info || item, startPosition: position || 0 });
    },

    async playEpisode(seriesId, season, num, position) {
        const info = await catalog.seriesInfo(seriesId);
        if (!info) return;
        if (catalog.ageBlocked(info)) return notify.error(t('age.blockedToast', { label: parseAge(info.age).label }));
        let episode = catalog.findEpisode(info, season, num);
        if (!episode && info.seasons && info.seasons[0]) episode = info.seasons[0].episodes[0];
        if (!episode) return;
        player.open({ mode: 'episode', series: info, episode, startPosition: position || 0 });
    },

    playFromCatalog(kind, id) {
        if (kind === 'vod') {
            const p = catalog.getProgress('vod', id);
            return this.playMovie(id, p && !p.finished ? p.position : 0);
        }
        if (kind === 'series') {
            const p = catalog.seriesProgress(id);
            if (p && !p.finished) return this.playEpisode(id, p.season, p.episode, p.position);
            return this.playEpisode(id, 1, 1, 0);
        }
        if (kind === 'live') return this.playLive(id);
        return null;
    },

    async toggleFavorite(kind, id) {
        // El conjunto cambia al instante; guardar y sincronizar puede tardar (o fallar): el botón no espera a eso.
        const pending = catalog.toggleFavorite(kind, id);
        refreshFavButtons(document, kind, id);
        let added;
        try {
            added = await pending;
        } catch (e) {
            console.warn('[favoritos]', e);
            refreshFavButtons(document, kind, id);
            notify.error(t('common.errorLoad'));
            return;
        }
        notify.toast(added ? t('common.addedToList') : t('common.removedFromList'), { icon: added ? 'bookmark_added' : 'bookmark_remove', duration: 1500 });
    },

    /**
     * Fija el espectador activo tras iniciar sesión: el elegido, o el único que hay (con varios se pregunta en
     * «¿Quién está viendo?»). Al arrancar ya se hace; sin esto, quien entraba sin recargar veía una «?».
     */
    refreshViewer() {
        if (account.enabled && !account.signedIn) return;
        viewers.ensureDefault(account.user ? account.user.name : t('who.me'));
        let viewer = viewers.current();
        if (!viewer && viewers.list().length === 1) {
            viewer = viewers.list()[0];
            viewers.setCurrent(viewer.id);
        }
        catalog.viewer = viewer || viewers.list()[0] || null;
        const nav = document.querySelector('[data-nav-avatar]');
        if (nav) nav.innerHTML = avatarHtml(catalog.viewer, 'is-small');
    },

    /** Elige espectador: recarga sus datos (lista, progreso) sin volver a descargar el catálogo. */
    /** Pantalla de términos: guarda la aceptación (y la manda al servidor si hay cuenta) y sigue a donde tocaba al arrancar. */
    async acceptTerms() {
        const version = currentTermsVersion(account);
        const viaServer = !!(account.signedIn && account.user && account.user.terms && !account.user.terms.accepted);
        if (viaServer) await account.acceptTerms(); // si falla (sin red), la pantalla avisa y no se pasa
        saveTermsRecord(version);
        if (viaServer) { location.reload(); return; } // el arranque se repite ya con el servicio disponible
        const next = afterTerms || '/';
        if (next === '/who/') whoAuto.pending = true;
        document.querySelector('.appnav').classList.toggle('is-hidden', next === '/login/' || next === '/start/' || next === '/who/' || next === '/welcome/');
        app.views.main.router.navigate(next, { reloadAll: true, clearPreviousHistory: true });
    },

    async selectViewer(id) {
        const viewer = viewers.get(id);
        if (!viewer) return;
        // Si se elige otro perfil antes de que acabe el anterior, manda el último.
        const seq = ++selectViewerSeq;
        viewers.setCurrent(id);
        await catalog.setViewer(viewer);
        if (seq !== selectViewerSeq) return;
        const nav = document.querySelector('[data-nav-avatar]');
        if (nav) nav.innerHTML = avatarHtml(viewer, 'is-small');
        app.views.main.router.navigate('/', { reloadAll: true, clearPreviousHistory: true });
    },

    askUnlock() {
        if (catalog.parentalUnlocked) return Promise.resolve(true);
        if (catalog.isKids) {
            notify.error(t('who.kidsLocked'));
            return Promise.resolve(false);
        }
        return new Promise((resolve) => {
            app.dialog.password(t('settings.parentalEnterPin'), t('settings.parental'), async (pin) => {
                const ok = await catalog.unlock(pin);
                if (!ok) notify.error(t('settings.parentalWrong'));
                resolve(ok);
            }, () => resolve(false));
        });
    },

    askNewPin() {
        return new Promise((resolve) => {
            app.dialog.password(t('settings.parentalSetPin'), t('settings.parental'), (pin1) => {
                if (!/^\d{4,6}$/.test(pin1 || '')) {
                    notify.error(t('settings.parentalSetPin'));
                    resolve(null);
                    return;
                }
                app.dialog.password(t('settings.parentalConfirmPin'), t('settings.parental'), (pin2) => {
                    if (pin1 !== pin2) {
                        notify.error(t('settings.parentalMismatch'));
                        resolve(null);
                    } else {
                        resolve(pin1);
                    }
                }, () => resolve(null));
            }, () => resolve(null));
        });
    },

    async setLanguage(code) {
        settings.set('lang', code);
        const lang = code === 'auto' ? i18n.detect() : code;
        await loadLocale(lang); // el diccionario del idioma nuevo, si aún no está (fichero aparte)
        i18n.setLang(lang);
        // Reconstruir la UI con las nuevas cadenas.
        document.querySelector('.appnav').outerHTML = navHtml();
        app.views.main.router.navigate(app.views.main.router.currentRoute.url, { reloadCurrent: true, ignoreCache: true });
        if (player.isOpen) player.close();
    },

    applyUiSettings() {
        const s = settings.all();
        i18n.setLang(s.lang === 'auto' ? i18n.detect() : s.lang);
        device.applyMode(s.tvMode, s.fireLayout === true);
        // En TV el tamaño de la interfaz lo da la escala de la página entera, no la letra.
        document.documentElement.style.setProperty('--ui-scale', String(device.isTV ? 1 : s.uiScale || 1));
        tvscale.apply(device.isTV, s.uiScale || 1);
        document.documentElement.classList.toggle('cards-poster-only', !!s.cardsPosterOnly);
        // Menú superior sobre la cabecera de Inicio: transparencia elegida en Ajustes (0–100 %).
        const fade = Number.isFinite(Number(s.navFade)) ? Math.max(0, Math.min(100, Number(s.navFade))) : 33;
        const navA = (0.9 * (100 - fade)) / 100;
        document.documentElement.style.setProperty('--nav-over-a', navA.toFixed(3));
        document.documentElement.style.setProperty('--nav-over-b', (navA * 0.47).toFixed(3));
        if (s.skin && s.skin !== 'default') document.documentElement.setAttribute('data-skin', s.skin);
        else document.documentElement.removeAttribute('data-skin');
        // Aspecto (shell): «cine» es el diseño original y respeta el tema oscuro/claro; los demás traen su base y sus formas.
        // El color de acento elegido (skin) se mantiene por encima del shell.
        themeMode = s.theme || 'dark';
        applyShell(shellById(s.shell || 'cine'), { themeMode, systemDark: !darkMedia || darkMedia.matches, accentOverride: s.skin && s.skin !== 'default' ? skinById(s.skin).accent : null });
        if (device.isTV || !device.hasTouch) tvnav.enable();
        initPeek(); // tarjeta ampliada al pararse sobre una carátula (ratón o mando)
        initWarmup(); // preparar el canal (lista HLS) o la ficha al pararse sobre ellos
    },

    async signOut() {
        if (account.demo) return; // la demo no se cierra a mano
        if (player.isOpen) player.close();
        catalog.flushSync();
        const list = profiles.list();
        await account.signOut();
        catalog.viewer = null;
        for (const p of list) await catalog.purgeProfile(p.id);
        await catalog.use(null);
        // Si ya se está en la pantalla de acceso (cuenta sin proveedores), navegar a la misma ruta no la repinta.
        if (/^\/login\/?$/.test(app.views.main.router.currentRoute.path)) {
            location.reload();
            return;
        }
        app.views.main.router.navigate('/login/', { reloadAll: true, clearPreviousHistory: true, ignoreCache: true });
    }
};

function back() {
    if (player && player.isOpen && !player.minimized) {
        player.handleBack(); // primero cierra lo que esté abierto sobre el vídeo (la tira de canales, un panel); si no, sale
        return;
    }
    // La vista «En pantalla» ocupa toda la pantalla: «atrás» la deja antes de salir de la página.
    if (window.IPTV && window.IPTV.stageBack && !document.querySelector('.modal-in:not(.toast)') && window.IPTV.stageBack()) return;
    // Atrás cierra el de más arriba (el último abierto): un menú ⋯ sobre un popup se cierra solo, sin llevarse el popup.
    const modal = Array.from(document.querySelectorAll('.modal-in:not(.toast):not(.modal-out)')).pop();
    if (modal) {
        // El asistente «Añadir fuente» (ventana modal) retrocede primero un paso dentro de sí mismo.
        if (modal.classList.contains('wiz-popup') && window.IPTV && typeof window.IPTV.pageBack === 'function' && window.IPTV.pageBack()) return;
        if (modal.classList.contains('order-popup') && window.IPTV && typeof window.IPTV.pageBack === 'function' && window.IPTV.pageBack()) return;
        // El asistente «Añadir fuente» (ventana modal) retrocede primero un paso dentro de sí mismo.
        if (modal.classList.contains('wiz-popup') && window.IPTV && typeof window.IPTV.pageBack === 'function' && window.IPTV.pageBack()) return;
        if (modal.classList.contains('dialog')) app.dialog.close(modal);
        else if (modal.classList.contains('actions-modal')) app.actions.close(modal);
        else if (modal.classList.contains('popup')) app.popup.close(modal);
        else if (modal.classList.contains('sheet-modal')) app.sheet.close(modal);
        else if (modal.classList.contains('popover')) app.popover.close(modal);
        return;
    }
    const router = app.views.main.router;
    // Ajustes con aspecto Fire TV: dentro de un apartado, «atrás» vuelve a la rejilla antes de salir de la página.
    if (router.currentRoute && /^\/settings\/?$/.test(String(router.currentRoute.url).split('?')[0]) && window.IPTV && typeof window.IPTV.settingsBack === 'function' && window.IPTV.settingsBack()) return;
    // Página de inicio con el asistente: «atrás» retrocede primero un paso dentro de él.
    if (router.currentRoute && /^\/start\/?$/.test(String(router.currentRoute.url).split('?')[0]) && window.IPTV && typeof window.IPTV.pageBack === 'function' && window.IPTV.pageBack()) return;
    // Pantalla de términos: no hay a dónde volver. En las apps, «atrás» pregunta si salir; en la web no hace nada.
    if (router.currentRoute && /^\/terms\/?$/.test(String(router.currentRoute.url).split('?')[0])) {
        if (device.isCordova || device.isTV || device.isElectron) app.dialog.confirm(t('exit.text'), t('exit.title'), () => { if (navigator.app && navigator.app.exitApp) navigator.app.exitApp(); else if (device.isElectron) window.close(); });
        return;
    }
    if (router.history.length > 1) {
        router.back();
        return;
    }
    const url = router.currentRoute ? router.currentRoute.url : '/';
    if (url !== '/' && url !== '/login/') {
        navigateSection('/');
        return;
    }
    if (device.isCordova || device.isTV || device.isElectron) {
        app.dialog.confirm(t('exit.text'), t('exit.title'), () => {
            if (navigator.app && navigator.app.exitApp) navigator.app.exitApp();
            else if (device.isElectron) window.close();
        });
    }
}

/* ---------------- Delegación de eventos ---------------- */

function bindGlobalClicks() {
    // Enter / la flecha «Ir» del teclado en pantalla (Android TV) dentro de un formulario de ventana (clave de OMDb, de TMDB…): sin esto el
    // navegador envía el formulario y recarga la app. Nunca debe recargar; en un diálogo equivale a pulsar su botón principal.
    document.addEventListener('submit', (e) => {
        const form = e.target;
        if (!form || form.tagName !== 'FORM') return;
        e.preventDefault();
        const dlg = form.closest && form.closest('.dialog');
        if (dlg && form.classList.contains('pw-dialog')) {
            const ok = dlg.querySelector('.dialog-button-bold') || dlg.querySelector('.dialog-button');
            if (ok) ok.click();
        }
    }, true);
    document.getElementById('app').addEventListener('click', (e) => {
        const target = e.target;
        const nav = target.closest('[data-nav]');
        if (nav) {
            e.preventDefault();
            navigateSection(nav.getAttribute('data-href') || '/');
            return;
        }
        const fav = target.closest('[data-fav]');
        if (fav) {
            e.preventDefault();
            e.stopPropagation();
            const [kind, id] = fav.getAttribute('data-fav').split(':');
            actions.toggleFavorite(kind, id);
            return;
        }
        const download = target.closest('[data-offline]');
        if (download) {
            e.preventDefault();
            e.stopPropagation();
            handleOfflineClick(download);
            return;
        }
        const cardMenu = target.closest('[data-card-menu]');
        if (cardMenu) {
            e.preventDefault();
            e.stopPropagation();
            const [kind, id] = cardMenu.getAttribute('data-card-menu').split(':');
            openCardMenu(kind, id);
            return;
        }
        const live = target.closest('[data-play-live]');
        if (live) {
            e.preventDefault();
            actions.playLive(live.getAttribute('data-play-live'));
            return;
        }
        const play = target.closest('[data-play]');
        if (play) {
            e.preventDefault();
            const [kind, id] = play.getAttribute('data-play').split(':');
            if (kind === 'vod') actions.playMovie(id, Number(play.getAttribute('data-resume-pos')) || 0);
            else if (kind === 'series') actions.playFromCatalog('series', id);
            return;
        }
        const ep = target.closest('[data-play-episode]');
        if (ep) {
            e.preventDefault();
            const [sid, season, num] = ep.getAttribute('data-play-episode').split(':');
            actions.playEpisode(sid, Number(season), Number(num), Number(ep.getAttribute('data-resume-pos')) || 0);
            return;
        }
        const resume = target.closest('[data-resume]');
        if (resume) {
            e.preventDefault();
            const parts = resume.getAttribute('data-resume').split(':');
            if (parts[0] === 'vod') {
                const p = catalog.getProgress('vod', parts[1]);
                actions.playMovie(parts[1], p ? p.position : 0);
            } else {
                const p = catalog.getProgress('episode', '');
                const prog = Object.keys(catalog.progress).map((k) => catalog.progress[k]).find((x) => x.seriesId === parts[1] && x.season === Number(parts[2]) && x.episode === Number(parts[3]));
                actions.playEpisode(parts[1], Number(parts[2]), Number(parts[3]), prog ? prog.position : (p ? p.position : 0));
            }
            return;
        }
        const castToggle = target.closest('[data-cast-toggle]');
        if (castToggle) {
            e.preventDefault();
            const list = castToggle.closest('.cast-list');
            const expanded = list.classList.toggle('is-expanded');
            castToggle.innerHTML = `<span class="mi">${expanded ? 'expand_less' : 'expand_more'}</span>${escapeHtml(castToggle.getAttribute(expanded ? 'data-label-less' : 'data-label-more'))}`;
            // El carrusel de caras solo se ve (y sus fotos solo se piden) cuando se despliega el reparto.
            const faces = list.parentElement && list.parentElement.querySelector('.cast-carousel');
            if (faces) {
                faces.hidden = !expanded;
                if (expanded) { faces.scrollLeft = 0; lazyImages(faces); }
            }
            return;
        }
        const actor = target.closest('[data-actor]');
        if (actor) {
            e.preventDefault();
            app.views.main.router.navigate('/actor/' + encodeURIComponent(actor.getAttribute('data-actor')) + '/');
            return;
        }
        const coll = target.closest('[data-collection]');
        if (coll) {
            e.preventDefault();
            openCollection(coll.getAttribute('data-collection'));
            return;
        }
        if (target.closest('[data-news-seen]')) {
            e.preventDefault();
            catalog.markLibraryNewsSeen();
            return;
        }
        // Solo fichas de películas y series: cualquier otro tipo no tiene ficha (antes un canal acababa en /series/<id>).
        const open = target.closest('[data-open^="vod:"], [data-open^="series:"]');
        if (open) {
            e.preventDefault();
            const [kind, id] = open.getAttribute('data-open').split(':');
            // Los títulos de alrededor, para pasar de ficha deslizando el dedo.
            const list = open.closest('.row-scroller, .grid, .chlist, .section, [data-results], [data-list]') || open.parentElement;
            setDetailSiblings(kind, Array.from(list ? list.querySelectorAll('[data-open^="' + kind + ':"]') : []).map((a) => a.getAttribute('data-open').slice(kind.length + 1)));
            const url = kind === 'vod' ? '/movie/' + id + '/' : '/series/' + id + '/';
            // Ficha abierta ya antes en esta cadena (Interstellar → Dune → Interstellar): se vuelve a ella en vez de apilarla otra
            // vez, así «atrás» no recorre el bucle entero.
            const hist = app.views.main.router.history;
            if (hist.indexOf(url) !== -1 && hist[hist.length - 1] !== url) app.views.main.router.back(url, { force: true });
            else app.views.main.router.navigate(url);
            return;
        }
        const menu = target.closest('[data-menu]');
        if (menu) {
            e.preventDefault();
            openMainMenu(menu);
            return;
        }
        const href = target.closest('[data-href]');
        if (href) {
            e.preventDefault();
            const url = href.getAttribute('data-href');
            // «Fuentes» (aviso de suscripción del Inicio): Ajustes ya en la pestaña Fuentes, no en la última que se usó.
            if (url === '/settings/?tab=sources') {
                const here = app.views.main.router.currentRoute && /^\/settings\/?$/.test(app.views.main.router.currentRoute.url.split('?')[0]);
                preferSettingsTab('sources');
                if (here) { const tab = document.querySelector('.page-current [data-settings-tab="sources"]'); if (tab) tab.click(); } else navigateSection('/settings/');
                return;
            }
            // «Añadir proveedor» (cabecera del Inicio): Ajustes en la pestaña Fuentes y, ya allí, el asistente «Añadir fuente».
            if (url === '/settings/?add=source') {
                const here = app.views.main.router.currentRoute && /^\/settings\/?$/.test(app.views.main.router.currentRoute.url.split('?')[0]);
                preferSettingsTab('sources');
                if (here) { const tab = document.querySelector('.page-current [data-settings-tab="sources"]'); if (tab) tab.click(); } else navigateSection('/settings/');
                setTimeout(() => openAddSource(), here ? 0 : 450);
                return;
            }
            if (/^\/(live|movies|series|search|mylist|settings|pair|who|guide|recordings|offline)\/?$/.test(url.split('?')[0]) || url === '/') navigateSection(url);
            else app.views.main.router.navigate(url);
            return;
        }
        const retry = target.closest('[data-retry-catalog]');
        if (retry) {
            e.preventDefault();
            catalog.refresh();
            return;
        }
        if (target.closest('[data-back]')) {
            e.preventDefault();
            back();
        }
    });
}

/* ---------------- Arranque ---------------- */

/* Tema claro/oscuro: 'auto' sigue al sistema (prefers-color-scheme). Lo aplica applyShell desde applyUiSettings. */
const darkMedia = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
let themeMode = 'dark';
if (darkMedia && darkMedia.addEventListener) darkMedia.addEventListener('change', () => { if (themeMode === 'auto') actions.applyUiSettings(); });

/* Enlace directo con el que se abrió la app (web): se aplica cuando hay sesión, espectador y catálogo. */
let pendingEntry = null;

function resumePosition(kind, id) {
    const last = storage.get('lastWatch', null);
    if (last && last.id === id && (last.mode === kind || (kind === 'episode' && last.mode === 'episode')) && Date.now() - (last.updated || 0) < 86400000) {
        if (last.duration && last.position / last.duration < 0.96) return last.position;
    }
    const p = catalog.getProgress(kind, id);
    return p && !p.finished ? p.position : 0;
}

/**
 * Espera a que aparezca el título de un enlace. Los proveedores añadidos se cargan después del principal, así que
 * un título que solo está en uno de ellos no está aún cuando arranca la app: se sigue mirando mientras carguen.
 */
function resolveWhenPresent(kind, token, timeoutMs = 45000) {
    return new Promise((resolve) => {
        let finished = false;
        let timer = null;
        const offs = [];
        const settled = () => {
            const want = catalog.extraProfiles ? catalog.extraProfiles().length : 0;
            const sources = Array.from(catalog.sources.values());
            return sources.length >= want && !sources.some((x) => x.loading);
        };
        const finish = (id) => {
            if (finished) return;
            finished = true;
            clearTimeout(timer);
            offs.forEach((off) => off());
            resolve(id);
        };
        const check = () => {
            const id = resolveToken(kind, token);
            if (id != null) finish(id);
            else if (settled()) finish(null); // ya no queda nada por cargar: el título no existe
        };
        offs.push(catalog.on('change', check), catalog.on('sources', check));
        timer = setTimeout(() => finish(null), timeoutMs);
        check();
    });
}

async function applyEntry() {
    const entry = pendingEntry;
    if (!entry) return;
    pendingEntry = null;
    const router = app.views.main.router;
    const go = async (route) => {
        if (route === '/' || (router.currentRoute && router.currentRoute.url === route)) return;
        router.navigate(route);
        // Deja terminar la transición: el reproductor se abre sobre la ficha o la sección, no sobre el inicio.
        await new Promise((resolve) => setTimeout(resolve, device.isTV ? 50 : 450));
    };
    if (entry.route) return go(entry.route);
    if (!(await whenCatalogLoaded())) { syncUrl(router.currentRoute ? router.currentRoute.url : '/'); return; }
    const id = await resolveWhenPresent(entry.kind, entry.token);
    if (id == null) {
        notify.error(t('common.noResults'));
        syncUrl(router.currentRoute ? router.currentRoute.url : '/');
        return;
    }
    if (entry.kind === 'vod') await go('/movie/' + encodeURIComponent(id) + '/');
    else if (entry.kind === 'series') await go('/series/' + encodeURIComponent(id) + '/');
    else if (entry.kind === 'live') await go('/live/');
    if (!entry.play) return;
    await Promise.race([catalog.pullProgress({ force: true }), new Promise((r) => setTimeout(r, 2500))]);
    if (entry.kind === 'vod') actions.playMovie(id, resumePosition('vod', id));
    else if (entry.kind === 'live') actions.playLive(id);
    else if (entry.kind === 'series') {
        const info = await catalog.seriesInfo(id);
        const ep = info && catalog.findEpisode(info, entry.season, entry.num);
        if (ep) actions.playEpisode(id, entry.season, entry.num, resumePosition('episode', ep.id));
    }
}

async function init() {
    settings.load();
    actions.applyUiSettings();
    // App de Windows: conversión de audio local (ffmpeg dentro de la app) para los MKV con Dolby/DTS.
    if (window.electronBridge) initLocalTranscode(window.electronBridge).catch(() => {});
    // Si el diccionario del idioma elegido llegó tarde (fichero aparte), se repinta con él.
    i18n.onLoaded = () => actions.setLanguage(settings.get('lang'));
    // Animación de arranque del logo (web/PWA: una vez por sesión y solo por la portada): va por encima mientras la app carga por detrás.
    maybeStartIntro({ web: true });
    // Segundo paso del inicio de sesión de los administradores (el servidor decide si hace falta).
    account.askSecondFactor = askSecondFactor;
    account.onRecoveryUsed = (n) => notify.toast(t('twofa.recoveryUsed', { n }), { icon: 'key', duration: 8000 });
    await account.init();
    // La demo pública es de solo lectura y sin descargas: se apagan antes de que nada las ofrezca o las recupere de una visita anterior.
    offline.disabled = !!account.demo;
    // La galería de la landing abre la demo con un aspecto o modo tele concretos: se aplican ya (los ajustes de interfaz se leyeron antes).
    if (account.demoUiChanged) { account.demoUiChanged = false; actions.applyUiSettings(); }
    // Demo pública: franja discreta con lo que es y cómo descargar la app; dentro de la landing (iframe), también «pantalla completa».
    const paintDemoBar = () => {
        let bar = document.getElementById('demo-bar');
        if (!account.demo) { if (bar) bar.remove(); return; }
        if (bar) return;
        bar = document.createElement('div');
        bar.id = 'demo-bar';
        bar.className = 'demo-bar';
        const framed = (() => { try { return window.top !== window; } catch (e) { return true; } })();
        bar.innerHTML = `<span class="mi">science</span><span class="demo-bar-text">${escapeHtml(t('demo.bar'))}</span>` +
            `<a class="demo-bar-link external" href="${escapeHtml(account.baseUrl())}/try/" target="_top" rel="noopener"><span class="mi">workspace_premium</span>${escapeHtml(t('demo.tryPremium'))}</a>` +
            '';
        document.body.appendChild(bar);
    };
    paintDemoBar();
    account.on('change', paintDemoBar);
    if (account.demoUnlimited) { const b = document.getElementById('demo-bar'); if (b && !b.querySelector('.demo-bar-test')) { const c = document.createElement('span'); c.className = 'demo-bar-left demo-bar-test'; c.textContent = t('demo.testMode'); b.insertBefore(c, b.querySelector('.demo-bar-link')); } }
    // Fin de la demo: pantalla opaca con la descarga (sin pasar por el inicio de sesión), al agotarse los minutos o el cupo del día.
    const demoEndScreen = (limited) => {
        if (document.getElementById('demo-end')) return;
        try { if (player.isOpen) player.close(); } catch (e) { /* sin reproductor */ }
        const el = document.createElement('div');
        el.id = 'demo-end';
        el.className = 'demo-end';
        const framed = (() => { try { return window.top !== window; } catch (e) { return true; } })();
        el.innerHTML = `<div class="demo-end-card"><span class="mi">timer_off</span><h2>${escapeHtml(t('demo.endedTitle'))}</h2><p>${escapeHtml(t(limited ? 'demo.limitText' : 'demo.endedText'))}</p>` +
            `<a class="btn btn-primary btn-lg external" href="${escapeHtml(account.baseUrl())}/try/" target="_top" rel="noopener"><span class="mi">workspace_premium</span>${escapeHtml(t('demo.tryPremium'))}</a>` +
            (framed ? '' : `<a class="btn btn-block external" href="${escapeHtml(account.baseUrl())}/" rel="noopener"><span class="mi">home</span>${escapeHtml(t('demo.backHome'))}</a>`) + '</div>';
        document.body.appendChild(el);
        const bar = document.getElementById('demo-bar'); if (bar) bar.remove();
    };
    // «La demo ha terminado» solo si se ha llegado desde la demo: quien abre /app/ a secas (con su cuenta) no debe verlo aunque su navegador recuerde una demo reciente.
    demoEndHook = demoEndScreen;
    account.on('demoended', () => demoEndScreen(false)); // la sesión de la demo caducó en el servidor mientras se usaba
    if (account.demoLimited || (account.demoEntry && account.demoEnded && !account.demo)) demoEndScreen(!!account.demoLimited);
    if (account.demo && account.demoExpiresAt) {
        const tick = () => {
            const left = account.demoExpiresAt - Date.now();
            const bar = document.getElementById('demo-bar');
            if (left <= 0) { clearInterval(demoTimer); account.endDemo(); demoEndScreen(false); return; }
            if (bar) { let c = bar.querySelector('.demo-bar-left'); if (!c) { c = document.createElement('span'); c.className = 'demo-bar-left'; bar.insertBefore(c, bar.querySelector('.demo-bar-link')); } const m = Math.floor(left / 60000); const s = Math.floor((left % 60000) / 1000); c.textContent = t('demo.left', { t: m + ':' + (s < 10 ? '0' : '') + s }); }
        };
        const demoTimer = setInterval(tick, 1000);
        tick();
    }
    // Fichas completadas con TMDB cuando el servidor tiene clave (reparto, director, fondo, edad…).
    // TMDB: la clave del servidor y el ajuste «Fichas con TMDB» (Ajustes → Datos) deciden si se usa; se rehace al cambiar el ajuste.
    const applyTmdbMode = () => {
        const on = account.tmdb && !catalog._tmdbOff();
        catalog.enricher = on ? (kind, info) => account.tmdbLookup({ kind, title: info.name, year: info.year, tmdb: info.tmdb }) : null;
        catalog.trendingSource = on ? () => account.tmdbTrending() : null;
        catalog.personSource = on ? (name) => account.tmdbPerson(name) : null;
        catalog.seasonSource = on ? (tmdb, season) => account.tmdbSeason({ tmdb, season }) : null;
        catalog.searchSource = on ? (kind, q) => account.tmdbSearch(Object.assign({ kind }, q)) : null;
    };
    actions.applyTmdbMode = applyTmdbMode; // se ejecuta tras OMDb (el modo «usar OMDb» depende de él)
    // OMDb (opcional, con la clave del servidor): valoraciones de IMDb, Rotten Tomatoes y Metacritic.
    const applyOmdb = () => { catalog.omdbSource = account.omdb && settings.get('omdbOn') !== false ? (kind, info) => account.omdbLookup({ kind, title: info.name, year: info.year, imdb: info.imdb }) : null; };
    applyOmdb();
    applyTmdbMode();
    actions.applyOmdb = () => { applyOmdb(); applyTmdbMode(); };
    // Catálogo de Filoteca (Wikidata, CC0): completa reparto, dirección, géneros… por identificador exacto, sin pasar por proveedores.
    const catalogClient = createCatalogClient({ getBase: () => account.baseUrl(), getLocale: () => i18n.lang });
    Object.defineProperty(catalog, 'catalogSource', { configurable: true, get: () => (catalogClient.enabled() ? (kind, info) => catalogClient.lookup({ kind, imdb: info.imdb, tmdb: info.tmdb, needPlot: !info.plot }) : null), set() { /* lo decide el ajuste */ } });

    // «Top 10» con el contador propio: solo con cuenta en el servidor; sin ella la fila usa las tendencias de TMDB (si hay).
    catalog.playsSink = account.enabled ? (body) => account.recordPlay(body) : null;
    catalog.topSource = account.enabled ? () => account.topPlays() : null;
    // En la web, el inicio de sesión en una biblioteca http lo hace el servidor (contenido mixto).
    clientHooks.mediaServerAuth = account.online ? (body) => account.mediaServerAuth(body) : null;
    // Archivos propios: carpetas que el navegador no puede leer por sí mismo, y sus carátulas.
    clientHooks.filesFetch = account.online ? (body) => account.filesFetch(body) : null;
    net.streamToken = () => account.streamToken;
    net.credentialProxy = account.online ? (url) => account.baseUrl() + '/proxy?url=' + encodeURIComponent(url) + (account.streamToken ? '&st=' + encodeURIComponent(account.streamToken) : '') : null;
    clientHooks.lookup = account.tmdb ? (kind, item) => account.tmdbLookup({ kind, title: item.name, year: item.year }) : null;
    // Proveedores que se suman al catálogo del principal (se elige en Ajustes, por dispositivo).
    catalog.extraProfiles = () => {
        const ids = storage.get('includedProfiles', []) || [];
        return profiles.list().filter((p) => ids.indexOf(p.id) !== -1);
    };

    // Descargas para ver sin conexión: con audio Dolby/DTS se baja ya convertido por el servidor.
    await setupOffline({ sourceFor: (rec) => (rec.converted ? transcodeUrl(rec.raw, 0) : net.streamUrl(rec.raw)) }).catch((e) => console.warn('[offline]', e));
    offline.on('progress', () => refreshOfflineButtons(document));
    offline.on('change', (rec) => {
        refreshOfflineButtons(document);
        if (!rec || !offline.get(rec.key)) return;
        if (rec.status === 'done' && Date.now() - rec.finishedAt < 3000) notify.toast(t('offline.finished', { title: rec.title }), { icon: 'download_done', duration: 3000 });
        else if (rec.status === 'error') notify.error(t('offline.failed', { title: rec.title }));
    });

    const root = document.getElementById('app');
    root.insertAdjacentHTML('afterbegin', '<div class="view view-main"></div>' + navHtml());

    app = new Framework7({
        el: '#app',
        name: t('app.name'),
        theme: device.isIOS ? 'ios' : 'md',
        darkMode: true,
        routes: buildRoutes(),
        touch: { tapHold: false, disableContextMenu: device.isCordova, mdTouchRipple: false },
        dialog: { buttonOk: t('common.ok'), buttonCancel: t('common.cancel') },
        view: { browserHistory: false, stackPages: false, animate: !device.isTV, iosSwipeBack: device.isIOS, preloadPreviousPage: true },
        serviceWorker: {}
    });
    window.app = app;

    player = new PlayerUI();

    window.IPTV = { trail: { read: readTrail, clear: clearTrail }, openAddSource, openExternal, openCollection, openMatch, openReportsAdmin, app, catalog, epg, profiles, account, viewers, updater, settings, device, i18n, t, tvnav, player, actions, back, notify, offline, version: VERSION };
    // Al iniciar sesión (o cambiar los espectadores desde otro dispositivo) se vuelve a fijar quién está viendo.
    account.on('change', () => { if (account.signedIn) actions.refreshViewer(); });

    const needsAccount = account.enabled && !account.signedIn;
    const wantsPair = /^\/pair\/?$/.test(appPath(location.pathname)) && account.enabled;
    const needsLogin = needsAccount || (!wantsPair && !profiles.current());
    // Espectador activo: el elegido, o el único que hay. Con varios se pregunta "¿Quién está viendo?".
    if (!needsAccount) viewers.ensureDefault(account.user ? account.user.name : t('who.me'));
    let viewer = viewers.current();
    if (!viewer && viewers.list().length === 1) {
        viewer = viewers.list()[0];
        viewers.setCurrent(viewer.id);
    }
    catalog.viewer = viewer || viewers.list()[0] || null;
    const navAvatar = document.querySelector('[data-nav-avatar]'); // la demo pública no tiene selector de espectadores
    if (navAvatar) navAvatar.innerHTML = avatarHtml(catalog.viewer, 'is-small');
    const askWho = !needsLogin && !wantsPair && viewers.list().length > 1;
    if (urlSyncEnabled() && !wantsPair) pendingEntry = parseEntryUrl(location.pathname, location.search);
    // Secciones sin dependencia del catálogo se abren directamente; el resto, tras cargarlo.
    const direct = pendingEntry && pendingEntry.route && !needsLogin && !askWho ? pendingEntry.route : null;
    if (direct) { pendingEntry = null; skipIntro(); }
    // Sin conexión y con algo descargado: se abre directamente en Descargas.
    const offlineStart = !needsLogin && !wantsPair && !askWho && !direct && (navigator.onLine === false || !account.online) && offline.hasDone();
    // Primera vez (sin ninguna fuente): la bienvenida, que termina en el inicio de sesión / «Añadir proveedor».
    const showWelcome = shouldShowWelcome({ standalone: false, needsLogin, needsAccount, wantsPair, providers: profiles.list().length, done: welcomeDone(), entryHome: !pendingEntry && !direct && !location.search && /^\/?(index\.html)?$/.test(location.pathname) });
    const initial = showWelcome ? '/welcome/' : needsLogin ? firstScreen({ needsAccount }) : wantsPair ? '/pair/' : askWho ? '/who/' : offlineStart ? '/offline/' : (direct || '/');
    // Términos de uso: la primera pantalla de todas. Sin aceptarlos no se entra (y, con cuenta, el servidor tampoco deja).
    afterTerms = initial;
    const startRoute = needsTermsGate({ record: termsRecord(), account }) && !(account.demoEntry && !account.demo) ? '/terms/' : initial; // la demo terminada va a «la demo ha terminado», no a los términos ni al login
    if (startRoute === '/who/') whoAuto.pending = true; // al arrancar, si nadie elige, entra solo el último espectador
    if (offlineStart) {
        pendingEntry = null;
        setTimeout(() => notify.toast(t('offline.startup'), { icon: 'wifi_off', duration: 3500 }), 800);
    }

    // Términos de uso: si el servidor exige una versión nueva con la app abierta, se vuelve a la pantalla de términos.
    const showTerms = () => {
        if (!account.termsPending) return;
        const r = app.views.main.router.currentRoute;
        if (r && /^\/terms\/?$/.test(String(r.url).split('?')[0])) return;
        afterTerms = '/';
        document.querySelector('.appnav').classList.add('is-hidden');
        app.views.main.router.navigate('/terms/', { reloadAll: true, clearPreviousHistory: true });
    };
    account.on('terms', showTerms);
    account.on('change', showTerms);

    account.on('signedout', () => {
        if (account.demoEntry) { if (demoEndHook) demoEndHook(!!account.demoLimited); return; }
        if (app.views.main.router.currentRoute.url === '/login/') return;
        catalog.use(null);
        app.views.main.router.navigate('/login/', { reloadAll: true, clearPreviousHistory: true, ignoreCache: true });
    });

    // Modales de Framework7 (hojas de acciones, diálogos, popups): con mando el foco pasa dentro
    // al abrirse y vuelve a la página al cerrarse.
    // ('modalOpen'/'modalClose' saltan al instante; los 'Opened' dependen de la transición CSS.)
    app.on('modalOpen', (modal) => {
        if (!device.isTV || !modal || !modal.el || modal.el.classList.contains('toast')) return;
        const focusIn = () => {
            if (!modal.el) return; // la ventana se cerró y se destruyó antes de este reintento
            const f = document.querySelector('.is-focused');
            if (!f || !modal.el.contains(f)) tvnav.focusLayer(modal.el);
        };
        // Dos intentos: en TVs lentas el diálogo puede tardar en tener tamaño.
        setTimeout(focusIn, 120);
        setTimeout(focusIn, 600);
    });
    app.on('modalClose', (modal) => {
        if (!device.isTV || !modal || !modal.el || modal.el.classList.contains('toast')) return;
        setTimeout(() => {
            if (player && player.isOpen && !player.minimized) return;
            const layer = Array.from(document.querySelectorAll('.modal-in:not(.toast):not(.modal-out)')).filter((el) => el !== modal.el && el.getBoundingClientRect().width > 0).pop() || document.querySelector('.page-current');
            if (layer) tvnav.focusLayer(layer);
        }, 120);
    });

    app.on('pageAfterIn', (page) => {
        sweepGhostModals();
        coach.hide();
        const mod = page.route && page.route.route && page.route.route.mod;
        const isLogin = page.name === 'login' || page.name === 'start' || page.name === 'who' || page.name === 'welcome' || page.name === 'terms';
        // Con un enlace pendiente (F5 en una ficha) la barra de direcciones no se toca hasta abrirlo: si se
        // reescribiera con «/» mientras carga el catálogo, se perdería dónde estaba el usuario.
        const opening = !!(pendingEntry && !isLogin && page.name !== 'pair');
        if (!opening && !(player && player.isOpen && !player.minimized)) syncUrl(page.route && page.route.url);
        if (opening) applyEntry().catch(() => { syncUrl(app.views.main.router.currentRoute.url); });
        document.querySelector('.appnav').classList.toggle('is-hidden', isLogin);
        if (mod) setActiveNav(mod.nav);
        const content = page.el.querySelector('.page-content');
        if (content) {
            const nav = document.querySelector('.appnav');
            const onScroll = () => {
                if (!document.contains(page.el) || page.el.classList.contains('page-previous')) return; // una página que ya no se ve no debe tocar el menú
                nav.classList.toggle('is-scrolled', content.scrollTop > 40);
                // Cabecera a pantalla completa: la de Inicio y de Películas/Series (clase de la página) o la de las fichas (su propia sección).
                nav.classList.toggle('over-hero', content.scrollTop <= 40 && (page.el.classList.contains('has-hero-top') || !!content.querySelector('.detail-hero')));
            };
            content.addEventListener('scroll', onScroll, { passive: true });
            page.el.addEventListener('hero-top', onScroll);
            setTimeout(onScroll, 250); // al terminar la transición ya se sabe qué página es la visible
            if (/detail/.test(page.name || '') && typeof MutationObserver !== 'undefined') {
                let t = null;
                new MutationObserver(() => { clearTimeout(t); t = setTimeout(onScroll, 120); }).observe(content, { childList: true, subtree: true });
            }
            onScroll();
        }
        if (device.isTV) setTimeout(() => tvnav.focusLayer(page.el), 120);
        // Ayuda en pantalla: sin animación ni diálogos encima y con el usuario aún en esa pantalla.
        if (!isLogin && coach.shouldShow(page.name, { isTV: device.isTV })) {
            setTimeout(() => {
                if (introActive() || document.querySelector('.modal-in') || (player && player.isOpen && !player.minimized)) return;
                const cur = app.views.main.router.currentRoute;
                if (!page.el || !page.el.classList.contains('page-current') || (cur && page.route && cur.url !== page.route.url)) return;
                coach.show(page.name, page.el, { isTV: device.isTV });
            }, 1400);
        }
    });
    document.querySelector('.appnav').classList.toggle('is-hidden', startRoute === '/terms/' || startRoute === '/login/' || startRoute === '/start/' || startRoute === '/who/' || startRoute === '/welcome/');
    const view = app.views.create('.view-main', { url: startRoute });
    startTrail(app); // rastro de diagnóstico: ya existe la vista principal y su router

    bindGlobalClicks();
    // Shell «Escenario»: la carátula enfocada (o bajo el ratón) ilumina el fondo de toda la pantalla con su imagen y atenúa el resto de su fila.
    {
        const html = document.documentElement;
        let litRow = null;
        const light = (card) => {
            if (!html.classList.contains('layout-ground-stage')) return;
            const row = card.closest('.row-scroller, .grid');
            if (litRow && litRow !== row) litRow.classList.remove('is-lit');
            litRow = row;
            if (row) row.classList.add('is-lit');
            const img = card.querySelector('.card-poster img');
            const src = img && (img.currentSrc || img.src);
            if (src && /^(https?:|blob:|data:image)/.test(src) && !/["'()\\]/.test(src)) html.style.setProperty('--stage-art', 'url("' + src + '")');
        };
        const cardOf = (e) => (e.target && e.target.closest ? e.target.closest('.card') : null);
        document.addEventListener('focusin', (e) => { const c = cardOf(e); if (c) light(c); }, true);
        document.addEventListener('mouseover', (e) => { const c = cardOf(e); if (c) light(c); }, true);
        document.addEventListener('mouseout', (e) => {
            if (!litRow || cardOf(e) == null) return;
            const to = e.relatedTarget && e.relatedTarget.closest ? e.relatedTarget.closest('.card') : null;
            if (!to && !litRow.querySelector('.card.is-focused')) { litRow.classList.remove('is-lit'); litRow = null; }
        }, true);
    }
    tvnav.onBack = () => back();
    // OK mantenido sobre una tarjeta (mando de la tele): opciones del canal o del título; el «+» de la tarjeta no se alcanza con el mando.
    tvnav.longPressSelector = '.card, .chitem, .mitem';
    tvnav.onLongPress = (el) => {
        const live = el.getAttribute('data-play-live');
        if (live) return openChannelMenu(live, el);
        const open = el.getAttribute('data-open');
        if (!open) return;
        const [kind, id] = open.split(':');
        if (kind === 'vod' || kind === 'series') openCardMenu(kind, id, { inContinue: !!el.querySelector('[data-card-menu]') });
    };
    // Pulsación larga con el dedo (o botón derecho) sobre un canal: el mismo menú, con «mover» si está en una hilera.
    bindCardLongPress({ selector: '.card-channel', onLongPress: (el) => { const live = el.getAttribute('data-play-live'); if (live) openChannelMenu(live, el); } });
    // Red de seguridad: un aparato que no se reconoce como tele (p. ej. un TV box raro) pero recibe teclas de mando
    // (flechas / OK) pasa a modo TV al momento, para que la navegación funcione aunque falle la detección.
    if (!device.isTV && settings.get('tvMode') === 'auto') {
        const DPAD = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
        let touched = false; // si alguien ha tocado la pantalla de verdad, es un móvil o una tableta: no se toca nada
        const onTouch = (e) => { if (e.pointerType === 'touch') { touched = true; document.removeEventListener('pointerdown', onTouch, true); } };
        document.addEventListener('pointerdown', onTouch, true);
        const onDpad = (e) => {
            if (device.isTV || settings.get('tvMode') !== 'auto' || touched) { document.removeEventListener('keydown', onDpad, true); return; }
            if (DPAD.indexOf(e.key) === -1) return;
            const tgt = e.target;
            if (tgt && /^(INPUT|TEXTAREA|SELECT)$/.test(tgt.tagName)) return;
            // Pantalla pequeña = móvil con teclado, no un mando (un Fire TV con escala 2 da 960×540 px CSS).
            if (Math.max(screen.width, screen.height) < 960) return;
            document.removeEventListener('keydown', onDpad, true);
            device.isTVAuto = true;
            actions.applyUiSettings();
            setTimeout(() => tvnav.focusLayer(tvnav.layer()), 50);
            console.warn('[init] modo TV activado por teclas de mando (UA: ' + navigator.userAgent.slice(0, 80) + ')');
        };
        document.addEventListener('keydown', onDpad, true);
    }
    // OK sobre un campo de texto en la tele: escribir con el móvil (QR, desde cualquier red) o con el teclado de la tele.
    tvnav.onTextField = (el) => {
        if (!(device.isTV || document.documentElement.classList.contains('mode-tv')) || el.hasAttribute('data-no-relay') || el.readOnly || el.disabled) return false;
        if (settings.get('relayTyping') === false) return false;
        const open = () => { import('./pages/relay-dialog.js').then((m) => m.openRelayTyping(el)).catch(() => { el.focus(); }); };
        const kb = () => { try { el.focus(); } catch (e) { /* nada */ } };
        app.actions.create({ cssClass: 'main-menu', buttons: [
            [{ text: t('relay.choice.title'), label: true },
             { text: '<span class="mi">smartphone</span> ' + t('relay.choice.phone'), bold: true, onClick: open },
             { text: '<span class="mi">keyboard</span> ' + t('relay.choice.tv'), onClick: kb }],
            [{ text: t('common.cancel'), color: 'red' }]
        ] }).open();
        return true;
    };
    tvnav.layerResolver = () => {
        if (player && player.isOpen && !player.minimized) return player.side ? player.el.side : player.root;
        // Un modal que se está cerrando (modal-out) o ya no se ve no cuenta como capa.
        // El último abierto es el de más arriba (un menú sobre un popup, un diálogo sobre un menú…): ese es el que maneja el mando.
        const modal = Array.from(document.querySelectorAll('.modal-in:not(.toast):not(.modal-out)')).filter((el) => el.getBoundingClientRect().width > 0).pop();
        if (modal) return modal;
        return document.querySelector('.page-current') || document.body;
    };

    const profile = profiles.current();
    if (profile && !needsLogin) actions.activateProfile(profile);

    /** Lleva a la fila «Novedades en tus bibliotecas» (está en Inicio) y la deja a la vista, resaltada un momento. */
    const showLibraryNews = () => {
        const go = () => {
            const rowEl = document.querySelector('.page-current [data-row-id="libnews"]');
            if (!rowEl) return false;
            rowEl.scrollIntoView({ block: 'start', behavior: 'smooth' });
            rowEl.classList.add('is-spotlight');
            setTimeout(() => rowEl.classList.remove('is-spotlight'), 2600);
            const first = rowEl.querySelector('.card, [data-open]');
            if (first && (device.isTV || document.documentElement.classList.contains('mode-tv')) && tvnav) tvnav.focus(first);
            return true;
        };
        const cur = app.views.main.router.currentRoute;
        if (!(cur && /^\/?$/.test(cur.url || '')) ) app.views.main.router.navigate('/');
        let tries = 0;
        const wait = setInterval(() => { if (go() || ++tries > 20) clearInterval(wait); }, 250);
    };

    // Aviso (una vez por sesión) de lo nuevo en las bibliotecas propias, cuando el catálogo ha dejado de moverse.
    let newsChecked = false;
    let newsTimer = null;
    catalog.on('change', () => {
        if (newsChecked || !catalog.loaded) return;
        clearTimeout(newsTimer);
        newsTimer = setTimeout(() => {
            if (newsChecked) return;
            newsChecked = true;
            const n = catalog.libraryNews(200).length;
            if (n) notify.toast(t('news.toast', { n }), { icon: 'new_releases', duration: 9000, action: { text: t('news.see'), onClick: showLibraryNews } });
        }, 5000);
    });

    document.addEventListener('visibilitychange', () => {
        if (document.hidden) catalog.flushSync();
        else catalog.pullProgress().catch(() => {});
    });
    window.addEventListener('pagehide', () => catalog.flushSync());
    player.on('open', hidePeek); // la tarjeta ampliada (y su vista previa) no sigue abierta sobre el reproductor
    player.on('close', () => setTimeout(() => catalog.flushSync(), 50));
    // La barra de direcciones refleja lo que se reproduce (/watch/…): F5 o compartir el enlace vuelve a abrirlo.
    initSharedUse({ player, notify, t });
    beatSources.push(inviteWebBeat);
    initUiSounds({ player });
    initTizen();
    startAutoRefresh({ catalog, settings, player });
    account.on('newshared', (list) => notify.toast(list.length === 1 ? t('share.newOne', { name: list[0].name || t('share.sharedName'), who: list[0].sharedBy || '' }) : t('share.newMany', { n: list.length }), { icon: 'group', duration: 6000 }));
    player.on('open', () => { skipIntro(); syncUrl(watchUrl(player)); });
    player.on('change', () => syncUrl(watchUrl(player)));
    player.on('close', () => { const r = app.views.main.router.currentRoute; syncUrl(r ? r.url : '/'); });
    player.on('minimize', () => { const r = app.views.main.router.currentRoute; syncUrl(r ? r.url : '/'); });
    player.on('restore', () => syncUrl(watchUrl(player)));
    // App nativa (APK/Electron): el bundle va dentro; si el servidor publica otra versión se propone actualizar.
    updater.current = VERSION;
    if (device.isCordova || device.isElectron) updater.checkOnStart();
    else if (/^https?:/.test(location.protocol)) updater.startWebWatcher();
    window.addEventListener('online', () => notify.toast(t('common.online'), { icon: 'wifi' }));
    window.addEventListener('offline', () => notify.toast(t('common.offline'), { icon: 'wifi_off' }));
    // Lo retirado tras denuncias de contenido (no se muestra); se vuelve a pedir al volver a la app.
    contentBlock.on('change', () => { if (catalog._primary) { catalog._merge(); catalog.emit('change'); } });
    const refreshBlocked = () => contentBlock.refresh(account.baseUrl());
    refreshBlocked();
    document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshBlocked(); });
    // Respuestas de soporte sin leer (con la sesión de la cuenta; la app independiente lo hace con su licencia).
    if (account.signedIn && account.support) {
        support.checkUnread().then((n) => {
            if (n > 0) notify.toast(t('support.unreadToast', { n }), { icon: 'support_agent', duration: 6000 });
        }).catch(() => {});
    }
    document.addEventListener('backbutton', (e) => { e.preventDefault(); back(); }, false);
    window.addEventListener('keydown', (e) => {
        // Atajo global de búsqueda en escritorio ("/")
        if (e.key === '/' && !(player.isOpen && !player.minimized) && !/^(INPUT|TEXTAREA)$/.test(document.activeElement.tagName)) {
            e.preventDefault();
            navigateSection('/search/');
        }
    });

    // hls.js se carga bajo demanda; se precarga en segundo plano pasados unos segundos para que
    // el primer canal en directo no tenga que esperar la descarga.
    if (!(device.isIOS && device.nativeHls)) setTimeout(() => ensureHls().catch(() => {}), 6000);

    if ('serviceWorker' in navigator && /^https?:/.test(location.protocol) && location.search.indexOf('nosw') === -1 && !device.isElectron) {
        navigator.serviceWorker.register('sw.js').catch(() => {});
    }

    const splash = document.getElementById('app-splash');
    if (splash) {
        splash.classList.add('is-hidden');
        setTimeout(() => splash.remove(), 400);
    }
    void view;
}

function boot() {
    if (window.cordova) {
        document.addEventListener('deviceready', init, false);
    } else if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
}

boot();

// Depuración: exponer helpers en consola.
window.__IPTV_DEBUG__ = { escapeHtml };
